import { ReactNode, useState, useMemo, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { prefetchRoute } from '@/utils/routePrefetch'
import { useNotificationCounts } from '@/hooks/useNotificationCounts'
import { websocketService } from '@/services/websocketService'
import NotificationBell from '@/components/notifications/NotificationBell'
import VoiceCommandModal from '@/components/voice/VoiceCommandModal'
// Removed CSS module import to use Tailwind directly

interface DashboardLayoutProps {
  children: ReactNode
}

// Helper hook for media queries
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const media = window.matchMedia(query)
    const listener = () => setMatches(media.matches)
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [query])
  return matches
}

// Custom SVG Icons - Matte Gold Thin-Line
const ShieldIcon = () => (
  <svg className="w-full h-full text-blue-600" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M50 10 L20 20 L20 45 C20 65 35 80 50 90 C65 80 80 65 80 45 L80 20 Z"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M50 25 L35 30 L35 45 C35 55 42 62 50 70 C58 62 65 55 65 45 L65 30 Z"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />
    <circle cx="50" cy="40" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
  </svg>
)

const NavIcon = ({ children }: { children: ReactNode }) => (
  <svg className="w-5 h-5 shrink-0 text-gray-500 stroke-[1.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    {children}
  </svg>
)

const DashboardIcon = () => (
  <NavIcon>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </NavIcon>
)

const CalendarIcon = () => (
  <NavIcon>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </NavIcon>
)

const MessageIcon = () => (
  <NavIcon>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </NavIcon>
)

const TaskIcon = () => (
  <NavIcon>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </NavIcon>
)

const ChartIcon = () => (
  <NavIcon>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </NavIcon>
)

const RouteIcon = () => (
  <NavIcon>
    <circle cx="5" cy="19" r="3" />
    <circle cx="19" cy="5" r="3" />
    <path d="M8 19l8-14" />
  </NavIcon>
)

const DispatchLayersIcon = () => (
  <NavIcon>
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </NavIcon>
)

const PackageIcon = () => (
  <NavIcon>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </NavIcon>
)

const UsersIcon = () => (
  <NavIcon>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </NavIcon>
)

const FileIcon = () => (
  <NavIcon>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </NavIcon>
)

const ClipboardListIcon = () => (
  <NavIcon>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 12h6M9 16h6M9 8h2" />
  </NavIcon>
)

const ComputerIcon = () => (
  <NavIcon>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </NavIcon>
)

const VehicleIcon = () => (
  <NavIcon>
    <path d="M5 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM19 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" />
    <path d="M17 17H7v-4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4z" />
    <path d="M7 9h10l-1-4H8l-1 4z" />
  </NavIcon>
)

const LogoutIcon = () => (
  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

const UserIcon = () => (
  <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const HeaderIcon = ({ children }: { children: ReactNode }) => (
  <svg className="w-5 h-5 text-gray-500 hover:text-blue-600 transition-colors duration-200 cursor-pointer" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    {children}
  </svg>
)

const SettingsIcon = () => (
  <HeaderIcon>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3m15.364 6.364l-4.243-4.243m-4.242 0L5.636 17.364m12.728 0l-4.243-4.243m-4.242 0L5.636 6.636" />
  </HeaderIcon>
)

const VoiceMicIcon = () => (
  <svg className="w-5 h-5 text-gray-900" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
)

const MenuIcon = () => (
  <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
)

const CloseIcon = () => (
  <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

// Icon mapping
const iconMap: Record<string, () => JSX.Element> = {
  'Kontrol Paneli': DashboardIcon,
  Vardiyalar: CalendarIcon,
  Mesajlar: MessageIcon,
  Görevler: TaskIcon,
  Analiz: ChartIcon,
  'Rota Optimizasyonu': RouteIcon,
  'Görev Dağıtımı': DispatchLayersIcon,
  'Araç Takibi': VehicleIcon,
  'Depo Yönetimi': PackageIcon,
  'Kullanıcı Yönetimi': UsersIcon,
  'Belge Arşiv': FileIcon,
  'Yazı İşleri Evrak': ClipboardListIcon,
  'Bilgi İşlem Talepleri': ComputerIcon,
  'Beyaz Masa Talepleri': MessageIcon,
  'Başkan Analiz Panosu': ChartIcon,
  'Beyaz Masa Paneli': ChartIcon,
  'Beyaz Masa İstatistikleri': ChartIcon,
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { unreadMessages, pendingTasks } = useNotificationCounts()

  // Real-time notification badge updates via WebSocket
  useEffect(() => {
    if (!user?.id) return

    const handleNewMessage = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadMessages'] })
    }
    const handleTaskEvent = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'pendingTasks'] })
    }

    websocketService.on('message:new', handleNewMessage)
    websocketService.on('task:created', handleTaskEvent)
    websocketService.on('task:updated', handleTaskEvent)
    websocketService.on('task:deleted', handleTaskEvent)
    websocketService.on('task:completed', handleTaskEvent)

    return () => {
      websocketService.off('message:new', handleNewMessage)
      websocketService.off('task:created', handleTaskEvent)
      websocketService.off('task:updated', handleTaskEvent)
      websocketService.off('task:deleted', handleTaskEvent)
      websocketService.off('task:completed', handleTaskEvent)
    }
  }, [user?.id, queryClient])

  const isPresident = user?.role === 'PRESIDENT' || user?.role === 'BASKAN'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const allNavigationItems = [
    { name: 'Kontrol Paneli', href: '/dashboard', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN', 'ADMIN'] },
    { name: 'Başkan Analiz Panosu', href: '/president-analytics', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN'] },
    { name: 'Vardiyalar', href: '/shifts', requiredRole: 'MEMUR' },
    { name: 'Mesajlar', href: '/messages', requiredRole: 'MEMUR', badge: unreadMessages },
    { name: 'Görevler', href: '/tasks', requiredRole: 'MEMUR', badge: pendingTasks },
    { name: 'Yazı İşleri Evrak', href: '/yazi-isleri', requiredRole: 'PERSONEL' },
    { name: 'Analiz', href: '/analysis', requiredRole: 'SUPERVISOR' },
    { name: 'Rota Optimizasyonu', href: '/route-optimization', requiredRole: 'SUPERVISOR' },
    { name: 'Görev Dağıtımı', href: '/dispatch', requiredRole: 'SUPERVISOR' },
    { name: 'Araç Takibi', href: '/vehicles', requiredRole: 'MESSENGER' }, // Visible to MESSENGER and above via hierarchy
    { name: 'Depo Yönetimi', href: '/warehouse', requiredRole: 'DEPO_KULLANICISI' },
    { name: 'Kullanıcı Yönetimi', href: '/users', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN', 'ADMIN'] },
    { name: 'Belge Arşiv', href: '/document-archive', requiredRole: 'BASKAN_YARDIMCISI' },
    { name: 'Bilgi İşlem Talepleri', href: '/it-tickets', requiredRole: 'PERSONEL' },
    { name: 'Beyaz Masa Talepleri', href: '/beyaz-masa-tickets', requiredRole: 'PERSONEL' },
    { name: 'Beyaz Masa Paneli', href: '/beyaz-masa/dashboard', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN', 'ADMIN', 'BASKAN_YARDIMCISI', 'GENEL_SEKRETER', 'DAIRE_BASKANI'] },
    { name: 'Beyaz Masa İstatistikleri', href: '/beyaz-masa/stats', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN', 'ADMIN'] },
    { name: 'Sınıflandırma Geri Bildirimi', href: '/classification-feedback', requiredRole: 'PRESIDENT', strictRoles: ['PRESIDENT', 'BASKAN', 'ADMIN'] },
  ]

  const navigation = useMemo(() => {
    const roleHierarchy: Record<string, number> = {
      MESSENGER: 1,
      ISCI: 1.3,
      PERSONEL: 1.5,
      MEMUR: 1.8,
      OPERATOR: 2,
      DEPO_KULLANICISI: 2.5,
      DAIRE_BASKANI: 2.8,
      SUPERVISOR: 3,
      GENEL_SEKRETER: 3.5,
      BASKAN_YARDIMCISI: 4,
      ADMIN: 4.8,
      BASKAN: 5,
      PRESIDENT: 5,
      // Department roles - equivalent to PERSONEL level (1.5)
      FEN_ISLERI: 1.5,
      TEMIZLIK: 1.5,
      PARK_BAHCE: 1.5,
      IMAR_SEHIRCILIK: 1.5,
      CEVRE: 1.5,
      SU_KANALIZASYON: 1.5,
      ULASIM: 1.5,
      ITFAIYE: 1.5,
      ZABITA: 1.5,
      SOSYAL_YARDIM: 1.5,
      KULTUR_SOSYAL: 1.5,
      SPOR: 1.5,
      YAZIM_EVRAK: 1.5,
      MALI_HIZMETLER: 1.5,
      BILGI_ISLEM: 1.5,
      INSAN_KAYNAKLARI: 1.5,
      ETUD_PROJE: 1.5,
      HUKUK: 1.5,
      AFET_ISLERI: 1.5,
    }

    const userLevel = user?.role ? (roleHierarchy[user.role] || 0) : 0
    const userRole = user?.role || ''

    const filtered = allNavigationItems.filter(item => {
      // If strictRoles is defined, use strict role matching
      if (item.strictRoles) {
        return item.strictRoles.includes(userRole)
      }

      // Otherwise use hierarchy-based filtering
      const requiredLevel = roleHierarchy[item.requiredRole] || 0
      const hasAccess = userLevel >= requiredLevel
      
      // Special safeguard: Ensure high-level roles (PRESIDENT, BASKAN, ADMIN) always see vehicle tracking
      if (item.name === 'Araç Takibi' && (userRole === 'PRESIDENT' || userRole === 'BASKAN' || userRole === 'ADMIN')) {
        return true
      }
      
      return hasAccess
    })
    
    return filtered
  }, [user?.role])

  const getRoleLabel = (role?: string) => {
    const labels: Record<string, string> = {
      PRESIDENT: 'Başkan',
      ADMIN: 'Yönetici',
      BASKAN: 'Belediye Başkanı',
      BASKAN_YARDIMCISI: 'Başkan Yardımcısı',
      GENEL_SEKRETER: 'Genel Sekreter',
      SUPERVISOR: 'Denetçi',
      DAIRE_BASKANI: 'Daire Başkanı',
      DEPO_KULLANICISI: 'Depo Kullanıcısı',
      OPERATOR: 'Operatör',
      MEMUR: 'Memur',
      PERSONEL: 'Personel',
      ISCI: 'İşçi',
      MESSENGER: 'Mesajcı',
      FEN_ISLERI: 'Fen İşleri',
      TEMIZLIK: 'Temizlik İşleri',
      PARK_BAHCE: 'Park ve Bahçeler',
      IMAR_SEHIRCILIK: 'İmar ve Şehircilik',
      CEVRE: 'Çevre Koruma',
      SU_KANALIZASYON: 'Su ve Kanalizasyon',
      ULASIM: 'Ulaşım Hizmetleri',
      ITFAIYE: 'İtfaiye',
      ZABITA: 'Zabıta',
      SOSYAL_YARDIM: 'Sosyal Yardım',
      KULTUR_SOSYAL: 'Kültür ve Sosyal',
      SPOR: 'Gençlik ve Spor',
      YAZIM_EVRAK: 'Yazı İşleri',
      MALI_HIZMETLER: 'Mali Hizmetler',
      BILGI_ISLEM: 'Bilgi İşlem',
      INSAN_KAYNAKLARI: 'İnsan Kaynakları',
      ETUD_PROJE: 'Etüd Proje',
      HUKUK: 'Hukuk İşleri',
      AFET_ISLERI: 'Afet İşleri',
    }
    return labels[role || ''] || 'Kullanıcı'
  }

  return (
    <div className="min-h-screen flex bg-gray-50 text-gray-900 font-sans">
      {/* Mobile backdrop with AnimatePresence */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar with layout animations */}
      {/* On desktop, sidebar is static (normal flow), on mobile it's fixed with z-50 */}
      <motion.div
        className="fixed inset-y-0 left-0 z-[100] w-[280px] bg-gray-100 border-r border-gray-200 flex flex-col shrink-0 lg:static lg:z-0 lg:transform-none"
        initial={false}
        animate={isDesktop ? { x: 0 } : (sidebarOpen ? { x: 0 } : { x: "-100%" })}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 flex items-center gap-3">
          <motion.div
            className="flex items-center justify-center w-12 h-12 shrink-0"
            whileHover={{ rotate: 5, scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <ShieldIcon />
          </motion.div>
          <h1 className="text-lg font-semibold text-gray-900 tracking-wide">Oltu Belediyesi</h1>
          <button
            className="ml-auto p-1 text-gray-500 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="flex flex-col gap-1 m-0 p-0 list-none">
            {navigation.map((item, index) => {
              const IconComponent = iconMap[item.name] || DashboardIcon

              return (
                <li key={item.name} className="block">
                  <NavLink
                    to={item.href}
                    end
                    onMouseEnter={() => prefetchRoute(item.href)}
                    onFocus={() => prefetchRoute(item.href)}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 p-3 text-sm font-medium rounded-md transition-all duration-200 border border-transparent relative
                       ${isActive
                        ? 'bg-blue-50 text-blue-800 border-blue-600'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-200'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.div
                            layoutId="activeNavIndicator"
                            className="absolute inset-0 bg-blue-50 rounded-md border-l-[3px] border-blue-600 z-0"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          />
                        )}
                        <span className={`relative z-10 ${isActive ? 'text-blue-600' : ''}`}>
                          <IconComponent />
                        </span>
                        <span className="relative z-10">{item.name}</span>
                        {item.badge && item.badge > 0 && (
                          <span className="ml-auto bg-red-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full min-w-[20px] text-center relative z-10">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-5 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
              <UserIcon />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 m-0 mb-0.5 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 m-0 truncate">
                {getRoleLabel(user?.role)}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-transparent border border-gray-300 rounded-md text-gray-700 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:border-gray-400 hover:text-gray-900"
          >
            <LogoutIcon />
            Çıkış Yap
          </motion.button>

          {/* GBSoft Branding */}
          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <a
              href="https://gbsoftt.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-gray-400 no-underline transition-colors duration-200 hover:text-blue-600"
            >
              Developed by <span className="font-medium">GBSoft</span>
            </a>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white w-full lg:w-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-8 h-16 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 text-gray-500 bg-transparent border-none cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <MenuIcon />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 m-0 tracking-wide">Oltu Belediyesi Yönetim Paneli</h1>
          </div>
          <div className="flex items-center gap-4">
            {isPresident && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="flex items-center justify-center p-2 rounded-full border-none cursor-pointer shadow-sm hover:shadow-md transition-all duration-200 bg-gradient-to-br from-[#c9b07f] to-[#a89060]"
                onClick={() => setVoiceModalOpen(true)}
                title="Sesli Komut"
              >
                <VoiceMicIcon />
              </motion.button>
            )}
            <NotificationBell />
            <motion.button
              whileHover={{ rotate: 45 }}
              transition={{ duration: 0.2 }}
              className="bg-transparent border-none p-2 cursor-pointer flex items-center justify-center"
              onMouseEnter={() => prefetchRoute('/settings')}
              onFocus={() => prefetchRoute('/settings')}
              onClick={() => navigate('/settings')}
              title="Ayarlar"
            >
              <SettingsIcon />
            </motion.button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 bg-white">
          {children}
        </main>
      </div>

      {/* Floating Action Button for Voice Commands - President Only */}
      {isPresident && (
        <motion.button
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-8 right-8 w-16 h-16 rounded-full border-none cursor-pointer flex items-center justify-center shadow-lg transition-all duration-300 z-[1000] bg-gradient-to-br from-[#c9a227] to-[#9a7b0a] hover:shadow-xl text-white"
          onClick={() => setVoiceModalOpen(true)}
          title="Sesli Komut"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </motion.button>
      )}

      {/* Voice Command Modal for President */}
      {isPresident && (
        <VoiceCommandModal
          isOpen={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
        />
      )}
    </div>
  )
}
